import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Lightbulb, Clock, ClipboardCheck, Calculator, ShieldCheck, TrendingUp, ArrowRight, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/brand";
import ArticlesSection from "@/components/ArticlesSection";

const HERO = "https://images.unsplash.com/photo-1601315890099-8b9d20bd22ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const reasons = [
  { icon: Clock, title: "Préparer sa retraite", text: "Anticiper la transmission permet de partir au bon moment et de sécuriser le fruit d'une vie de travail." },
  { icon: TrendingUp, title: "Saisir une opportunité", text: "Un marché favorable ou un repreneur motivé peut maximiser la valeur de cession de votre entreprise." },
  { icon: Lightbulb, title: "Se lancer dans un nouveau projet", text: "Céder pour réinvestir son énergie ailleurs : un choix stratégique mûrement réfléchi." },
  { icon: ShieldCheck, title: "Assurer la pérennité", text: "Transmettre à un repreneur solide, c'est protéger ses salariés, ses clients et son héritage." },
];

const steps = [
  { n: "01", icon: Calculator, title: "Évaluer son entreprise", text: "Faites estimer votre société par un expert : chiffre d'affaires, EBITDA, actifs, potentiel de croissance et rentabilité." },
  { n: "02", icon: ClipboardCheck, title: "Préparer le dossier", text: "Constituez un dossier de présentation clair et attractif : chiffres, vidéo, atouts et perspectives." },
  { n: "03", icon: ShieldCheck, title: "Cibler les bons repreneurs", text: "Diffusez l'opportunité de manière confidentielle auprès d'acheteurs qualifiés et financièrement solides." },
  { n: "04", icon: TrendingUp, title: "Négocier & transmettre", text: "Accompagnement juridique et financier jusqu'à la signature, puis durant la période de transition." },
];

const faqs = [
  { q: "Quand est-ce le bon moment pour vendre ?", a: "Idéalement lorsque l'entreprise est en bonne santé financière et en croissance : c'est à ce moment qu'elle a le plus de valeur. Anticipez 2 à 3 ans à l'avance pour préparer sereinement la transmission." },
  { q: "Combien vaut mon entreprise ?", a: "La valorisation dépend de plusieurs critères : rentabilité (EBITDA), chiffre d'affaires, actifs, secteur, récurrence du chiffre d'affaires et perspectives. PROAC Transmission réalise une évaluation objective et gratuite de votre société." },
  { q: "La confidentialité est-elle garantie ?", a: "Oui. Les informations détaillées (chiffres, identité) ne sont accessibles qu'aux acheteurs enregistrés et qualifiés. Les annonces publiques restent anonymisées." },
  { q: "Combien de temps dure une transmission ?", a: "En moyenne 6 à 12 mois entre la mise en vente et la signature, selon la taille de l'entreprise, le secteur et le profil des repreneurs." },
  { q: "Quels sont les frais ?", a: "L'évaluation initiale est gratuite. Nos honoraires d'accompagnement sont transparents et majoritairement liés à la réussite de la cession." },
];

export default function Conseils() {
  const navigate = useNavigate();
  return (
    <div className="bg-[#FDFDFC]">
      <Header />

      <section className="relative h-[46vh] min-h-[340px] flex items-center">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1B2A4A]/82" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 w-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Conseils & expertise</span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight">Réussir la transmission de votre entreprise</h1>
            <p className="mt-5 text-white/80 text-base sm:text-lg max-w-xl">Pourquoi vendre, quand vendre, comment se préparer : nos conseils pour aborder la cession en toute sérénité.</p>
          </motion.div>
        </div>
      </section>

      {/* Pourquoi vendre */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-24">
        <div className="max-w-2xl">
          <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Pourquoi vendre ?</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Les bonnes raisons de céder</h2>
          <p className="mt-4 text-[#475569]">Chaque transmission répond à une motivation différente. Identifier la vôtre est la première étape d'une cession réussie.</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((r, i) => (
            <motion.div key={r.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-white border border-gray-200 p-7">
              <div className="w-12 h-12 bg-[#1B2A4A] flex items-center justify-center"><r.icon className="w-6 h-6 text-[#E0C173]" strokeWidth={1.5} /></div>
              <h3 className="mt-5 font-display text-lg text-[#1B2A4A]">{r.title}</h3>
              <p className="mt-2 text-sm text-[#475569] leading-relaxed">{r.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <ArticlesSection />

      {/* Quand vendre */}
      <section className="bg-[#1B2A4A] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Quand vendre ?</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight">Le timing fait la valeur</h2>
            <p className="mt-5 text-white/75 leading-relaxed">Le meilleur moment pour céder n'est pas quand l'entreprise décline, mais quand elle performe. Une société en croissance, rentable et bien organisée se vend mieux et plus cher.</p>
            <ul className="mt-8 space-y-4">
              {["Anticipez 2 à 3 ans avant la cession souhaitée", "Cédez lorsque les indicateurs (CA, rentabilité) sont au vert", "Préparez l'entreprise pour qu'elle fonctionne sans vous", "Profitez d'un marché et d'un secteur porteurs"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm"><span className="w-1.5 h-1.5 rounded-full bg-[#E0C173] mt-2 shrink-0" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[["2-3 ans", "d'anticipation idéale"], ["6-12 mois", "durée moyenne d'une cession"], ["12", "départements couverts"], ["x1,5", "valeur en période de croissance"], ["100%", "confidentialité"], ["Gratuit", "première évaluation"]].map(([n, l]) => (
              <div key={l} className="bg-[#1B2A4A] p-6 text-center">
                <div className="font-display text-2xl text-[#E0C173]">{n}</div>
                <div className="mt-1 text-[11px] uppercase tracking-wider text-white/60 leading-tight">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comment se préparer */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-24">
        <div className="max-w-2xl">
          <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Comment se préparer ?</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Les 4 étapes clés</h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="border-t-2 border-[#E0C173] bg-white p-7 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="font-display text-3xl text-gray-200">{s.n}</span>
                <s.icon className="w-6 h-6 text-[#E0C173]" strokeWidth={1.5} />
              </div>
              <h3 className="mt-5 font-display text-lg text-[#1B2A4A]">{s.title}</h3>
              <p className="mt-2 text-sm text-[#475569] leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-gray-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-24">
          <div className="text-center">
            <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Questions fréquentes</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Tout ce que vous devez savoir</h2>
          </div>
          <Accordion type="single" collapsible className="mt-12">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-gray-200">
                <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-display text-lg text-[#1B2A4A] hover:no-underline hover:text-[#E0C173]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#475569] leading-relaxed text-base">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0D1629]">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight">Prêt à transmettre votre entreprise ?</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">Échangez avec un conseiller PROAC Transmission pour une première évaluation gratuite et confidentielle.</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="conseils-seller-cta" onClick={() => navigate("/inscription?role=seller")} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-white px-8 py-6 text-base">
              Vendre mon entreprise <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
            <a href={`tel:${PHONE_TEL}`} data-testid="conseils-phone-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base transition-colors">
              <Phone className="w-4 h-4 text-[#E0C173]" /> {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
